import { normalizeClientType, resolvePriceClientType } from './clientType';
import { normalizeQuantity } from './quantity';
import { isFormatOfferPrice, isFormatOfferSelectable } from './formatOffers';

export const POS_PRICE_TYPES = ['detalle', 'mayorista'];

export const DEFAULT_BARCODE_PRICING_RULE = {
  mode: 'quantity',
  thresholds: {
    detalle: null,
    mayorista: null,
  },
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeRuleMode = (value) => (
  String(value || '').trim().toLowerCase() === 'subtotal' ? 'subtotal' : 'quantity'
);

const normalizeThreshold = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed);
};

export const normalizeBarcodePricingRule = (value, fallback = DEFAULT_BARCODE_PRICING_RULE) => {
  const source = value && typeof value === 'object' ? value : {};
  const base = fallback && typeof fallback === 'object' ? fallback : DEFAULT_BARCODE_PRICING_RULE;
  const sourceThresholds = source.thresholds && typeof source.thresholds === 'object'
    ? source.thresholds
    : {};
  const baseThresholds = base.thresholds && typeof base.thresholds === 'object'
    ? base.thresholds
    : DEFAULT_BARCODE_PRICING_RULE.thresholds;
  if (
    sourceThresholds.mayorista === undefined
    && sourceThresholds.importador !== undefined
  ) {
    sourceThresholds.mayorista = sourceThresholds.importador;
  }

  const formatCodes = new Set(POS_PRICE_TYPES);
  Object.keys(baseThresholds || {}).forEach((key) => {
    const code = normalizeClientType(key);
    if (code) formatCodes.add(code);
  });
  Object.keys(sourceThresholds || {}).forEach((key) => {
    const code = normalizeClientType(key);
    if (code) formatCodes.add(code);
  });

  const thresholds = {};
  formatCodes.forEach((type) => {
    const sourceValue = normalizeThreshold(sourceThresholds[type]);
    thresholds[type] = sourceValue === null ? normalizeThreshold(baseThresholds[type]) : sourceValue;
  });

  return {
    mode: normalizeRuleMode(source.mode ?? base.mode),
    thresholds,
  };
};

export const resolveVarietyBarcodePricingRule = (globalRule, varietyRuleOverride) => {
  const normalizedGlobal = normalizeBarcodePricingRule(globalRule, DEFAULT_BARCODE_PRICING_RULE);
  if (!varietyRuleOverride || typeof varietyRuleOverride !== 'object') return normalizedGlobal;
  return normalizeBarcodePricingRule(varietyRuleOverride, normalizedGlobal);
};

const buildResolutionKey = ({ varietyId, priceIndex, cartLineId }) => {
  if (cartLineId) return `line:${cartLineId}`;
  return `slot:${varietyId}:${priceIndex}`;
};

const byCheapest = (a, b) => {
  if (Number(a.price) !== Number(b.price)) return Number(a.price) - Number(b.price);
  return Number(a.priceIndex) - Number(b.priceIndex);
};

const byMostExpensive = (a, b) => {
  if (Number(a.price) !== Number(b.price)) return Number(b.price) - Number(a.price);
  return Number(a.priceIndex) - Number(b.priceIndex);
};

const pickCheapest = (candidates) => [...(candidates || [])].sort(byCheapest)[0] || null;
const pickMostExpensive = (candidates) => [...(candidates || [])].sort(byMostExpensive)[0] || null;

const resolveAutoCandidate = ({ candidates, metric, rule }) => {
  const normalizedRule = normalizeBarcodePricingRule(rule, DEFAULT_BARCODE_PRICING_RULE);
  const thresholds = normalizedRule.thresholds || {};
  const thresholdTypes = new Set([
    ...Object.keys(thresholds),
    ...(candidates || []).map((candidate) => candidate.priceType).filter(Boolean),
  ]);
  const hasThresholds = Array.from(thresholdTypes)
    .some((type) => thresholds[type] !== null && thresholds[type] !== undefined);
  if (!hasThresholds) return pickMostExpensive(candidates);

  const qualified = (candidates || []).filter((candidate) => {
    const threshold = thresholds[candidate.priceType];
    if (threshold === null || threshold === undefined) return false;
    return Number(metric || 0) >= Number(threshold);
  });
  return qualified.length ? pickCheapest(qualified) : pickMostExpensive(candidates);
};

const getSellablePriceFilter = (isSellablePrice) => (
  typeof isSellablePrice === 'function'
    ? isSellablePrice
    : (price) => price && price.price !== null && price.price !== undefined
);

const buildCandidates = (variety, isSellablePrice) => {
  const isSellable = getSellablePriceFilter(isSellablePrice);
  return (variety?.VarietyPrices || [])
    .map((price, priceIndex) => ({
      price,
      isFormatOffer: isFormatOfferPrice(price),
      selectable: isFormatOfferSelectable(price),
      priceIndex,
      priceValue: toNumber(price?.price) || 0,
      priceType: normalizeClientType(resolvePriceClientType(price)) || 'mayorista',
    }))
    .filter((candidate) => isSellable(candidate.price))
    .map((candidate) => ({
      ...candidate,
      price: candidate.priceValue,
    }));
};

const computeSubtotalBeforeDiscount = (rows) => Math.floor((rows || []).reduce((sum, row) => {
  const quantity = normalizeQuantity(row.quantity || 0);
  if (quantity <= 0) return sum;
  const unitPrice = toNumber(row.selectedCandidate?.price) || 0;
  return sum + Math.floor(unitPrice * quantity);
}, 0));

const resolveRuleMode = (row, fallbackMode) => {
  const mode = row?.rule?.mode;
  if (mode === 'subtotal' || mode === 'quantity') return mode;
  return fallbackMode === 'subtotal' ? 'subtotal' : 'quantity';
};

const applyAutoSelection = ({ rows, globalRule, quantityByVariety }) => {
  const fallbackMode = normalizeBarcodePricingRule(globalRule).mode;
  let iterations = 0;
  while (iterations < 6) {
    iterations += 1;
    const subtotalMetric = computeSubtotalBeforeDiscount(rows);
    let changed = false;

    rows.forEach((row) => {
      if (!row.autoPriceSelection || row.selectedCandidate?.isFormatOffer) return;
      const mode = resolveRuleMode(row, fallbackMode);
      const metric = mode === 'subtotal'
        ? subtotalMetric
        : (quantityByVariety.get(Number(row.varietyId)) || 0);
      const next = resolveAutoCandidate({
        candidates: row.candidates.filter((candidate) => !candidate.isFormatOffer && candidate.selectable),
        metric,
        rule: row.rule,
      });
      if (!next) return;
      if (Number(next.priceIndex) !== Number(row.selectedCandidate?.priceIndex)) {
        row.selectedCandidate = next;
        changed = true;
      }
    });

    if (!changed) break;
  }
};

export const resolvePosAutoPriceSelection = ({
  products = [],
  quantities = {},
  scaleReadingLines = [],
  linePriceMeta = {},
  globalRule = DEFAULT_BARCODE_PRICING_RULE,
  isSellablePrice,
  canSelectPriceType,
  lineKey = (varietyId, priceIndex) => `${varietyId}:${priceIndex}`,
} = {}) => {
  const normalizedGlobalRule = normalizeBarcodePricingRule(globalRule, DEFAULT_BARCODE_PRICING_RULE);
  const rows = [];
  const quantityByVariety = new Map();

  const addRow = ({ product, variety, sourcePriceIndex, quantity, cartLineId = null, source = 'matrix' }) => {
    const normalizedQuantity = normalizeQuantity(quantity || 0, 3);
    if (normalizedQuantity <= 0) return;
    const candidates = buildCandidates(variety, isSellablePrice);
    const selectedCandidate = candidates.find((candidate) => Number(candidate.priceIndex) === Number(sourcePriceIndex));
    if (!selectedCandidate) return;

    const key = lineKey(variety.id, sourcePriceIndex);
    const meta = linePriceMeta?.[key] || null;
    const selectable = typeof canSelectPriceType === 'function'
      ? Boolean(canSelectPriceType(variety))
      : Boolean(canSelectPriceType);
    const autoPriceSelection = selectable
      && Boolean(meta?.autoPriceSelection)
      && !Boolean(meta?.priceTypeOverride);

    rows.push({
      key: buildResolutionKey({ varietyId: variety.id, priceIndex: sourcePriceIndex, cartLineId }),
      source,
      cartLineId,
      product,
      variety,
      varietyId: variety.id,
      sourcePriceIndex,
      quantity: normalizedQuantity,
      candidates,
      selectedCandidate,
      autoPriceSelection,
      rule: resolveVarietyBarcodePricingRule(normalizedGlobalRule, variety?.priceAutoRuleOverride),
    });
    quantityByVariety.set(
      Number(variety.id),
      (quantityByVariety.get(Number(variety.id)) || 0) + normalizedQuantity
    );
  };

  (products || []).forEach((product) => {
    (product?.Varieties || []).forEach((variety) => {
      (variety?.VarietyPrices || []).forEach((_, priceIndex) => {
        addRow({
          product,
          variety,
          sourcePriceIndex: priceIndex,
          quantity: quantities?.[variety.id]?.[priceIndex],
          source: 'matrix',
        });
      });
    });
  });

  (scaleReadingLines || []).forEach((line) => {
    const product = (products || []).find((entry) =>
      (entry?.Varieties || []).some((variety) => Number(variety?.id) === Number(line?.varietyId))
    );
    const variety = (product?.Varieties || []).find(
      (entry) => Number(entry?.id) === Number(line?.varietyId)
    );
    if (!product || !variety) return;
    const sourcePriceIndex = Number.isInteger(line?.priceIndex)
      ? line.priceIndex
      : (variety.VarietyPrices || []).findIndex(
        (price) => Number(price?.id) === Number(line?.varietyPriceId)
      );
    if (!Number.isInteger(sourcePriceIndex) || sourcePriceIndex < 0) return;
    addRow({
      product,
      variety,
      sourcePriceIndex,
      quantity: line?.quantity,
      cartLineId: line?.cartLineId || null,
      source: 'scale',
    });
  });

  applyAutoSelection({
    rows,
    globalRule: normalizedGlobalRule,
    quantityByVariety,
  });

  const byKey = new Map();
  rows.forEach((row) => {
    const candidate = row.selectedCandidate;
    const priceRow = row.variety?.VarietyPrices?.[candidate?.priceIndex] || null;
    byKey.set(row.key, {
      ...row,
      priceIndex: candidate?.priceIndex,
      priceRow,
      selectedPrice: candidate?.price || 0,
      selectedPriceIndex: candidate?.priceIndex,
      selectedPriceType: candidate?.priceType,
    });
  });

  return {
    rows,
    byKey,
  };
};

export const getResolvedPosAutoPriceLine = (resolution, { varietyId, priceIndex, cartLineId } = {}) => {
  const key = buildResolutionKey({ varietyId, priceIndex, cartLineId });
  return resolution?.byKey?.get(key) || null;
};
